import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { getPost, createPost, updatePost, getCategories, getAuthors, generateSlug } from '../../api/admin/client';

export default function PostEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);

  const [form, setForm] = useState({
    title: '',
    subtitle: '',
    slug: '',
    content: '',
    excerpt: '',
    featuredImageUrl: '',
    metaDescription: '',
    seoTitle: '',
    status: 'draft',
    isFeatured: false,
    readTime: '5 min read',
    authorId: '',
    categoryId: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchOptions();
    if (!isNew) {
      fetchPost();
    }
  }, [id]);

  const fetchOptions = async () => {
    try {
      const [catData, authData] = await Promise.all([
        getCategories(),
        getAuthors()
      ]);
      setCategories(catData.categories || []);
      setAuthors(authData.authors || []);
    } catch (err) {
      console.error('Error fetching options:', err);
    }
  };

  const fetchPost = async () => {
    try {
      setLoading(true);
      const data = await getPost(id);
      setForm({
        title: data.post.title || '',
        subtitle: data.post.subtitle || '',
        slug: data.post.slug || '',
        content: data.post.content || '',
        excerpt: data.post.excerpt || '',
        featuredImageUrl: data.post.featuredImageUrl || '',
        metaDescription: data.post.metaDescription || '',
        seoTitle: data.post.seoTitle || '',
        status: data.post.status || 'draft',
        isFeatured: data.post.isFeatured || false,
        readTime: data.post.readTime || '5 min read',
        authorId: data.post.authorId || '',
        categoryId: data.post.categoryId || ''
      });
    } catch (err) {
      console.error('Error fetching post:', err);
      alert('Error loading post');
      navigate('/admin/posts');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-generate slug from title
    if (name === 'title' && isNew) {
      setForm(prev => ({
        ...prev,
        slug: generateSlug(value)
      }));
    }

    // Clear error when field changes
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.title.trim()) newErrors.title = 'Title is required';
    if (!form.slug.trim()) newErrors.slug = 'Slug is required';
    if (!form.content.trim()) newErrors.content = 'Content is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSaving(true);
      const data = {
        ...form,
        authorId: form.authorId || null,
        categoryId: form.categoryId || null
      };

      if (isNew) {
        const result = await createPost(data);
        navigate(`/admin/posts/${result.post.id}`);
      } else {
        await updatePost(id, data);
      }

      alert(isNew ? 'Post created!' : 'Post saved!');
    } catch (err) {
      alert('Error saving post: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setForm(prev => ({ ...prev, status: 'published' }));
    // Submit will happen via form
  };

  if (loading) {
    return (
      <AdminLayout title={isNew ? 'New Post' : 'Edit Post'}>
        <div className="admin-loading">
          <div className="admin-spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isNew ? 'New Post' : 'Edit Post'}>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '24px' }}>
          {/* Main Content */}
          <div>
            <div className="admin-card">
              <div className="admin-card-body">
                <div className="admin-form-group">
                  <label className="admin-label" htmlFor="title">Title *</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    className="admin-input"
                    value={form.title}
                    onChange={handleChange}
                    placeholder="Enter post title"
                    style={{ fontSize: '18px', fontWeight: 500 }}
                  />
                  {errors.title && <span style={{ color: 'var(--admin-danger)', fontSize: '13px' }}>{errors.title}</span>}
                </div>

                <div className="admin-form-group">
                  <label className="admin-label" htmlFor="subtitle">Subtitle</label>
                  <input
                    type="text"
                    id="subtitle"
                    name="subtitle"
                    className="admin-input"
                    value={form.subtitle}
                    onChange={handleChange}
                    placeholder="Optional subtitle"
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-label" htmlFor="slug">Slug *</label>
                  <input
                    type="text"
                    id="slug"
                    name="slug"
                    className="admin-input"
                    value={form.slug}
                    onChange={handleChange}
                    placeholder="post-url-slug"
                  />
                  {errors.slug && <span style={{ color: 'var(--admin-danger)', fontSize: '13px' }}>{errors.slug}</span>}
                </div>

                <div className="admin-form-group">
                  <label className="admin-label" htmlFor="content">Content *</label>
                  <textarea
                    id="content"
                    name="content"
                    className="admin-textarea"
                    value={form.content}
                    onChange={handleChange}
                    placeholder="Write your post content here... (Markdown supported)"
                    style={{ minHeight: '400px', fontFamily: 'monospace' }}
                  />
                  {errors.content && <span style={{ color: 'var(--admin-danger)', fontSize: '13px' }}>{errors.content}</span>}
                </div>

                <div className="admin-form-group">
                  <label className="admin-label" htmlFor="excerpt">Excerpt</label>
                  <textarea
                    id="excerpt"
                    name="excerpt"
                    className="admin-textarea"
                    value={form.excerpt}
                    onChange={handleChange}
                    placeholder="Brief summary for previews"
                    style={{ minHeight: '80px' }}
                  />
                </div>
              </div>
            </div>

            {/* SEO Section */}
            <div className="admin-card">
              <div className="admin-card-header">
                <h3 className="admin-card-title">SEO Settings</h3>
              </div>
              <div className="admin-card-body">
                <div className="admin-form-group">
                  <label className="admin-label" htmlFor="seoTitle">SEO Title</label>
                  <input
                    type="text"
                    id="seoTitle"
                    name="seoTitle"
                    className="admin-input"
                    value={form.seoTitle}
                    onChange={handleChange}
                    placeholder="Custom title for search engines"
                    maxLength={70}
                  />
                  <small className="admin-text-muted">{form.seoTitle.length}/70 characters</small>
                </div>

                <div className="admin-form-group">
                  <label className="admin-label" htmlFor="metaDescription">Meta Description</label>
                  <textarea
                    id="metaDescription"
                    name="metaDescription"
                    className="admin-textarea"
                    value={form.metaDescription}
                    onChange={handleChange}
                    placeholder="Description for search results"
                    style={{ minHeight: '80px' }}
                    maxLength={160}
                  />
                  <small className="admin-text-muted">{form.metaDescription.length}/160 characters</small>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div>
            {/* Publish Box */}
            <div className="admin-card">
              <div className="admin-card-header">
                <h3 className="admin-card-title">Publish</h3>
              </div>
              <div className="admin-card-body">
                <div className="admin-form-group">
                  <label className="admin-label" htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    className="admin-select"
                    value={form.status}
                    onChange={handleChange}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div className="admin-form-group">
                  <label className="admin-flex admin-items-center admin-gap-2" style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="isFeatured"
                      checked={form.isFeatured}
                      onChange={handleChange}
                    />
                    <span>Featured Post</span>
                  </label>
                </div>

                <div className="admin-flex admin-gap-2" style={{ marginTop: '16px' }}>
                  <button
                    type="submit"
                    className="admin-btn admin-btn-primary"
                    disabled={saving}
                    style={{ flex: 1 }}
                  >
                    {saving ? 'Saving...' : (isNew ? 'Create Post' : 'Save Changes')}
                  </button>
                </div>

                {!isNew && (
                  <button
                    type="button"
                    onClick={() => window.open(`/blog/${form.slug}`, '_blank')}
                    className="admin-btn admin-btn-secondary"
                    style={{ width: '100%', marginTop: '8px' }}
                  >
                    View Post
                  </button>
                )}
              </div>
            </div>

            {/* Category & Author */}
            <div className="admin-card">
              <div className="admin-card-header">
                <h3 className="admin-card-title">Organization</h3>
              </div>
              <div className="admin-card-body">
                <div className="admin-form-group">
                  <label className="admin-label" htmlFor="categoryId">Category</label>
                  <select
                    id="categoryId"
                    name="categoryId"
                    className="admin-select"
                    value={form.categoryId}
                    onChange={handleChange}
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div className="admin-form-group">
                  <label className="admin-label" htmlFor="authorId">Author</label>
                  <select
                    id="authorId"
                    name="authorId"
                    className="admin-select"
                    value={form.authorId}
                    onChange={handleChange}
                  >
                    <option value="">Select author</option>
                    {authors.map(author => (
                      <option key={author.id} value={author.id}>{author.name}</option>
                    ))}
                  </select>
                </div>

                <div className="admin-form-group">
                  <label className="admin-label" htmlFor="readTime">Read Time</label>
                  <input
                    type="text"
                    id="readTime"
                    name="readTime"
                    className="admin-input"
                    value={form.readTime}
                    onChange={handleChange}
                    placeholder="5 min read"
                  />
                </div>
              </div>
            </div>

            {/* Featured Image */}
            <div className="admin-card">
              <div className="admin-card-header">
                <h3 className="admin-card-title">Featured Image</h3>
              </div>
              <div className="admin-card-body">
                <div className="admin-form-group">
                  <input
                    type="text"
                    name="featuredImageUrl"
                    className="admin-input"
                    value={form.featuredImageUrl}
                    onChange={handleChange}
                    placeholder="Image URL"
                  />
                </div>
                {form.featuredImageUrl && (
                  <img
                    src={form.featuredImageUrl}
                    alt="Featured"
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      marginTop: '8px'
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
}
